import classNames from "classnames";
import styles from "./AdtechBanner.module.scss";
import { ESIInclude } from "@packages/esi-include/ESIInclude";
import { StandardProps } from "@/types/types";
import { Breadcrumb, FacetsInformation } from "@/types/productListing";
import { buildBannerPathname } from "./utils";
import { useRouteParams } from "@/hooks/useRouteParams";

type BannerProps = {
  pathname: string;
  adtechBannerService: string;
  facetsInformation: FacetsInformation;
  crumbs?: Breadcrumb[];
  searchTerm?: string;
};

const JL_EVENTS_ADTECH_BANNER_CLICK = "jl-events.adtech.bannerClick";

const dispatchClickEvent = () => {
  window.dispatchEvent(new CustomEvent(JL_EVENTS_ADTECH_BANNER_CLICK));
};

function AdtechBrowseBanner({
  pathname,
  adtechBannerService,
  facetsInformation,
  crumbs,
}: BannerProps) {
  const { appliedProductTypes, isFilteredByBrand } = facetsInformation;
  if (!Array.isArray(crumbs) || !crumbs?.length) return null;

  const formattedBreadcrumbs = encodeURIComponent(
    crumbs.map((b) => b.displayName.trim()).join("|"),
  );

  const appliedProductTypesParam =
    Array.isArray(appliedProductTypes) && appliedProductTypes.length
      ? `&appliedProductTypes=${appliedProductTypes.join("|")}`
      : "";

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={styles.adtechbanner}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={
          `${adtechBannerService}/browse` +
          `?browsePageHierarchy=${formattedBreadcrumbs}` +
          `&brandFilterApplied=${String(isFilteredByBrand)}` +
          `&pathname=${pathname}` +
          `${appliedProductTypesParam}`
        }
        id="esiAdtechBanner"
      />
    </aside>
  );
}

function AdtechSearchBanner({
  pathname,
  adtechBannerService,
  facetsInformation,
  searchTerm,
}: BannerProps) {
  const { isFiltered } = facetsInformation;
  if (!searchTerm) {
    return null;
  }

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanner)}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={
          `${adtechBannerService}/search` +
          `?searchTerm=${encodeURIComponent(searchTerm.toString())}` +
          `&filterApplied=${String(isFiltered)}` +
          `&pathname=${pathname}`
        }
        id="esiAdtechBanner"
      />
    </aside>
  );
}

function AdtechBrandBanner({
  adtechBannerService,
  brandName,
}: {
  adtechBannerService: string;
  brandName: string | undefined;
}) {
  if (!brandName) return null;

  return (
    <aside
      aria-label="Banner Ad"
      data-testid="banner-container"
      className={classNames(styles.container, styles.adtechbanner)}
      onClick={dispatchClickEvent}
    >
      <ESIInclude
        src={`${adtechBannerService}/brand?brandName=${encodeURIComponent(brandName)}`}
        id="esiAdtechBanner"
      />
    </aside>
  );
}

export function AdtechBanner({
  runtimeConfig,
  productListingData,
}: Pick<StandardProps, "runtimeConfig" | "productListingData">) {
  const { pageType, query, facetId } = useRouteParams();

  const { adtechBannerService } = runtimeConfig;
  const { crumbs, facetsInformation } = productListingData;

  const pathname = buildBannerPathname({ query, facetId });

  if (pageType === "browse") {
    return (
      <AdtechBrowseBanner
        pathname={pathname}
        adtechBannerService={adtechBannerService}
        facetsInformation={facetsInformation}
        crumbs={crumbs}
      />
    );
  }

  if (pageType === "search" && query["search-term"]) {
    return (
      <AdtechSearchBanner
        pathname={pathname}
        adtechBannerService={adtechBannerService}
        facetsInformation={facetsInformation}
        searchTerm={
          Array.isArray(query["search-term"])
            ? query["search-term"][0]
            : query["search-term"]
        }
      />
    );
  }

  if (pageType === "brand") {
    return (
      <AdtechBrandBanner
        brandName={productListingData.pageInformation.brandName}
        adtechBannerService={adtechBannerService}
      />
    );
  }

  return null;
}
